/**
 * ApiClient - Axios instance with token injection, error normalization,
 * cursor pagination helpers, and auto-retry on 401.
 *
 * Requirements: 1.8, 2.2
 */

import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';

// --- Types ---

export interface ApiError {
  status: number;
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    nextCursor?: string;
    hasNext: boolean;
  };
}

export interface CursorPageParams {
  cursor?: string;
  limit?: number;
  // Index signature so params objects are assignable to Record<string, unknown>
  // (required by fetchPage / axios param helpers). Subtypes add typed fields.
  [key: string]: unknown;
}

// --- Token management (set by AuthProvider) ---

let getSessionToken: (() => string | null) | null = null;
let refreshSessionToken: (() => Promise<string | null>) | null = null;

/**
 * Registers token accessors from the AuthProvider.
 * Called once during AuthProvider initialization.
 */
export function registerTokenHandlers(handlers: {
  getToken: () => string | null;
  refreshToken: () => Promise<string | null>;
}): void {
  getSessionToken = handlers.getToken;
  refreshSessionToken = handlers.refreshToken;
}

// --- Axios instance ---

const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// --- Request interceptor: inject Authorization header ---

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getSessionToken?.();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: unknown) => Promise.reject(error),
);

// --- Response interceptor: error normalization + 401 retry ---

let isRefreshing = false;
let refreshSubscribers: Array<(token: string | null) => void> = [];

function subscribeToRefresh(callback: (token: string | null) => void): void {
  refreshSubscribers.push(callback);
}

function onRefreshComplete(token: string | null): void {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined;

    // Auto-retry on 401 with token refresh
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      refreshSessionToken
    ) {
      originalRequest._retry = true;

      if (isRefreshing) {
        // Wait for the ongoing refresh to complete
        return new Promise((resolve, reject) => {
          subscribeToRefresh((token) => {
            if (token && originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(apiClient(originalRequest));
            } else {
              reject(normalizeError(error));
            }
          });
        });
      }

      isRefreshing = true;

      try {
        const newToken = await refreshSessionToken();
        isRefreshing = false;
        onRefreshComplete(newToken);

        if (newToken && originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return apiClient(originalRequest);
        }
      } catch {
        isRefreshing = false;
        onRefreshComplete(null);
      }
    }

    return Promise.reject(normalizeError(error));
  },
);

// --- Error normalization ---

function normalizeError(error: AxiosError): ApiError {
  if (error.response) {
    const data = error.response.data as Record<string, unknown> | undefined;
    // The backend wraps errors as `{ error: { statusCode, errorCode, message,
    // details } }` (formatErrorResponse / AppError.toResponse). Unwrap that
    // envelope and map `errorCode` → `code`. Fall back to a flat
    // `{ code, message, details }` shape for any endpoint that returns one.
    const enveloped =
      data && typeof data.error === 'object' && data.error !== null
        ? (data.error as Record<string, unknown>)
        : undefined;
    const src = enveloped ?? data;
    return {
      status: error.response.status,
      code: (src?.errorCode as string) || (src?.code as string) || 'UNKNOWN_ERROR',
      message: (src?.message as string) || error.message || 'An error occurred',
      details: (src?.details as Record<string, unknown>) || undefined,
    };
  }

  if (error.request) {
    return {
      status: 0,
      code: 'NETWORK_ERROR',
      message: 'Unable to reach the server. Please check your connection.',
    };
  }

  return {
    status: 0,
    code: 'REQUEST_ERROR',
    message: error.message || 'An unexpected error occurred',
  };
}

// --- Cursor pagination helpers ---

/**
 * Fetches a single page of paginated data.
 * Handles both standard format { data, pagination } and
 * backend format { posts/items/data, cursor }.
 */
export async function fetchPage<T>(
  url: string,
  params?: CursorPageParams & Record<string, unknown>,
  config?: AxiosRequestConfig,
): Promise<PaginatedResponse<T>> {
  const queryParams: Record<string, unknown> = { ...params };
  if (params?.cursor) {
    queryParams.cursor = params.cursor;
  }
  if (params?.limit) {
    queryParams.limit = params.limit;
  }

  const response = await apiClient.get(url, {
    ...config,
    params: queryParams,
  });

  const raw = response.data;

  // If response already has the expected format, return as-is
  if (raw?.pagination?.nextCursor !== undefined || raw?.pagination?.hasNext !== undefined) {
    return raw as PaginatedResponse<T>;
  }

  // Normalize backend responses: { posts, cursor } or { data, cursor } or { items, cursor }
  const items = raw?.data ?? raw?.posts ?? raw?.items ?? [];
  const cursor = raw?.pagination?.cursor ?? raw?.cursor ?? raw?.nextCursor;

  return {
    data: items as T[],
    pagination: {
      nextCursor: cursor ?? undefined,
      hasNext: !!cursor,
    },
  };
}

/**
 * Fetches all pages by following cursors until hasNext is false.
 * Use with caution — only for bounded datasets.
 */
export async function fetchAllPages<T>(
  url: string,
  params?: Omit<CursorPageParams, 'cursor'> & Record<string, unknown>,
  config?: AxiosRequestConfig,
): Promise<T[]> {
  const allItems: T[] = [];
  let cursor: string | undefined;
  let hasNext = true;

  while (hasNext) {
    const page = await fetchPage<T>(url, { ...params, cursor }, config);
    allItems.push(...page.data);
    cursor = page.pagination.nextCursor;
    hasNext = page.pagination.hasNext;
  }

  return allItems;
}

// --- Helper to check if an error is an ApiError ---

export function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    'code' in error &&
    'message' in error
  );
}

export default apiClient;
